import { functions, httpsCallable } from "@repo/ui";
import { Loader2, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

type Mode = "disabled" | "test" | "production";
type Policy = {
	mode: Mode;
	allowedNumbers: string[];
	allowedPrefixes: string[];
};

const getPolicy = httpsCallable<{ companyId: string }, { policy: Policy }>(
	functions,
	"getExternalOutboundExecutionPolicy",
);
const setPolicy = httpsCallable<
	{ companyId: string } & Policy,
	{ policy: Policy }
>(functions, "setExternalOutboundExecutionPolicy");

function lines(value: string) {
	return value
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean);
}

export function CompanyExternalExecutionPolicyCard({
	companyId,
}: {
	companyId: string;
}) {
	const [saved, setSaved] = useState<Policy | null>(null);
	const [mode, setMode] = useState<Mode>("disabled");
	const [numbers, setNumbers] = useState("");
	const [prefixes, setPrefixes] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [confirmProduction, setConfirmProduction] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await getPolicy({ companyId });
			setSaved(data.policy);
			setMode(data.policy.mode);
			setNumbers(data.policy.allowedNumbers.join("\n"));
			setPrefixes(data.policy.allowedPrefixes.join("\n"));
		} catch (error) {
			console.error("Failed to load outbound execution policy", error);
			toast.error("Could not load the outbound execution policy.");
		} finally {
			setLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		void load();
	}, [load]);

	const save = async () => {
		setSaving(true);
		try {
			const { data } = await setPolicy({
				companyId,
				mode,
				allowedNumbers: lines(numbers),
				allowedPrefixes: lines(prefixes),
			});
			setSaved(data.policy);
			setMode(data.policy.mode);
			setNumbers(data.policy.allowedNumbers.join("\n"));
			setPrefixes(data.policy.allowedPrefixes.join("\n"));
			toast.success("Outbound execution policy saved.");
		} catch (error) {
			console.error("Failed to save outbound execution policy", error);
			toast.error(
				error instanceof Error ? error.message : "Could not save the policy.",
			);
		} finally {
			setSaving(false);
			setConfirmProduction(false);
		}
	};

	const currentLabel =
		saved?.mode === "production"
			? "Production"
			: saved?.mode === "test"
				? "Test"
				: "Disabled";
	const dirty =
		!!saved &&
		(mode !== saved.mode ||
			numbers !== saved.allowedNumbers.join("\n") ||
			prefixes !== saved.allowedPrefixes.join("\n"));

	return (
		<Card>
			<CardHeader>
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="space-y-1.5">
						<CardTitle className="flex items-center gap-2">
							<ShieldCheck className="h-5 w-5" />
							Developer outbound calls
						</CardTitle>
						<CardDescription>
							Controls playbook runs created through the Developer API and MCP.
							Queued calls are checked again before dialing.
						</CardDescription>
					</div>
					{saved && (
						<Badge
							variant={
								saved.mode === "production" ? "destructive" : "secondary"
							}
						>
							Active: {currentLabel}
						</Badge>
					)}
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				{loading ? (
					<p className="flex items-center gap-2 text-sm text-slate-600">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading policy...
					</p>
				) : !saved ? (
					<Button variant="outline" onClick={() => void load()}>
						Retry loading policy
					</Button>
				) : (
					<>
						<div className="space-y-2">
							<Label htmlFor="outbound-execution-mode">Execution mode</Label>
							<Select
								value={mode}
								onValueChange={(value: Mode) => setMode(value)}
								disabled={saving}
							>
								<SelectTrigger
									id="outbound-execution-mode"
									className="max-w-sm"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="disabled">Disabled — deny all</SelectItem>
									<SelectItem value="test">
										Test — approved destinations only
									</SelectItem>
									<SelectItem value="production">
										Production — supported Israeli local numbers
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{mode === "test" && (
							<div className="grid gap-4 md:grid-cols-2">
								<div className="space-y-2">
									<Label htmlFor="approved-outbound-numbers">
										Approved numbers
									</Label>
									<Textarea
										id="approved-outbound-numbers"
										value={numbers}
										onChange={(event) => setNumbers(event.target.value)}
										disabled={saving}
										rows={5}
										className="font-mono text-sm"
										placeholder={"+972501234567\n+97221234567"}
									/>
									<p className="text-xs text-slate-600">
										One local number per line. Numbers are normalized when
										saved.
									</p>
								</div>
								<div className="space-y-2">
									<Label htmlFor="approved-outbound-prefixes">
										Approved prefixes
									</Label>
									<Textarea
										id="approved-outbound-prefixes"
										value={prefixes}
										onChange={(event) => setPrefixes(event.target.value)}
										disabled={saving}
										rows={5}
										className="font-mono text-sm"
										placeholder="+9725012345*"
									/>
									<p className="text-xs text-slate-600">
										One prefix per line, ending in *. Each can match at most 100
										local numbers.
									</p>
								</div>
							</div>
						)}
						<p className="text-xs text-slate-600">
							Developer runs are limited to 10 tasks per run, 50 new tasks per
							UTC day, and 3 attempts per task. The system dialer supports 02,
							03, 04, 05, 07, 08, and 09.
						</p>
						<Button
							disabled={!dirty || saving}
							onClick={() =>
								mode === "production" && saved.mode !== "production"
									? setConfirmProduction(true)
									: void save()
							}
						>
							{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							Save policy
						</Button>
					</>
				)}
			</CardContent>
			<AlertDialog open={confirmProduction} onOpenChange={setConfirmProduction}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Enable production outbound calls?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Developer keys with the runs:execute scope will be able to create
							playbook runs for supported Israeli local numbers. The policy is
							checked again before each call.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={() => void save()}>
							Enable production
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Card>
	);
}
